import {
	type ErrorInfo,
	type PointerEvent as ReactPointerEvent,
	type ReactNode,
	type RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import {
	JSM_PLAYGROUND_SIGNUP_INTENT_CHANGE_EVENT,
	type JsmPlaygroundTeamType,
	setJsmPlaygroundSignupIntent,
} from '@/components/signup/utils/jsm-playground-signup-intent';
import { wacToken } from '@/libraries/design/foundations';
import { Tab } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab';
import { TabList } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-list';
import { TabPanel } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-panel';
import { Cookie } from '@/utils/cookie';
import { useExperiment } from '@/utils/experiment/use-experiment';
import { wacRefreshJsm } from '@/utils/experiment/wac-refresh-experiments';
import { useLocation } from '@/utils/location-provider';

import { JsmPortalPlaygroundHrPreview } from './components/hr/jsm-portal-playground-hr-preview';
import { JsmPortalPlaygroundItSupportPreview } from './components/it-support/jsm-portal-playground-it-support-preview';
import { JsmPlaygroundCursor, type CursorColor } from './components/jsm-playground-cursor';
import { JsmPlaygroundDemoLayerContext } from './components/jsm-playground-demo-layer';
import { JsmPlaygroundNavigation } from './components/jsm-playground-navigation';
import { JsmPlaygroundSeeHowItWorks } from './components/jsm-playground-see-how-it-works';
import { JsmPlaygroundEngineering } from './engineering/jsm-playground-engineering';
import {
	JsmPlaygroundErrorBoundary,
	type FallbackRenderProps,
} from './jsm-playground-error-boundary';
import { JsmPlaygroundItOperations } from './jsm-playground-it-operations';
import { teamNavigationConfig, teamTabs, type TeamTabValue } from './jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as messages } from './jsm-portal-playground-messages';
import { useJsmPlaygroundAnalytics } from './use-jsm-playground-analytics';
import { useJsmPlaygroundSectionExploredAnalytics } from './use-jsm-playground-section-explored-analytics';

const PREVIEW_DESIGN_WIDTH = 1168;
const PREVIEW_DESIGN_HEIGHT = 750;
const STATIC_MOBILE_MEDIA_QUERY = '(max-width: 779px)';

const useIsStaticMobile = () => {
	const [isStaticMobile, setIsStaticMobile] = useState(false);

	useEffect(() => {
		const query = window.matchMedia?.(STATIC_MOBILE_MEDIA_QUERY);
		if (!query) {
			return;
		}

		const update = () => setIsStaticMobile(query.matches);
		update();
		query.addEventListener('change', update);
		return () => query.removeEventListener('change', update);
	}, []);

	return isStaticMobile;
};

const playgroundTeamTypes: Record<TeamTabValue, JsmPlaygroundTeamType> = {
	'it-support': 'it-support',
	'it-operations': 'operations',
	engineering: 'software-development',
	hr: 'human-resources',
};

const getPlaygroundTabForTeamType = (teamType: string | null): TeamTabValue | undefined =>
	Object.entries(playgroundTeamTypes).find(([, value]) => value === teamType)?.[0] as
		| TeamTabValue
		| undefined;

const teamTabIds = new Set<string>(teamTabs.map((teamTab) => teamTab.value));

const usePreviewScale = (): [RefObject<HTMLDivElement>, number] => {
	const ref = useRef<HTMLDivElement>(null);
	const [scale, setScale] = useState(1);

	useEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		const measure = () => {
			const width = element.clientWidth;
			if (width > 0) {
				setScale(Math.min(1, width / PREVIEW_DESIGN_WIDTH));
			}
		};

		const observer = new ResizeObserver(measure);
		observer.observe(element);
		measure();

		return () => observer.disconnect();
	}, []);

	return [ref, scale];
};

const DEMO_START_VISIBILITY = 0.5;

const useHasBeenSeen = (ref: RefObject<HTMLElement>, onVisible?: () => void) => {
	const [hasBeenSeen, setHasBeenSeen] = useState(false);
	const onVisibleRef = useRef(onVisible);
	onVisibleRef.current = onVisible;

	useEffect(() => {
		const element = ref.current;
		if (!element || hasBeenSeen) {
			return;
		}
		if (typeof IntersectionObserver === 'undefined') {
			setHasBeenSeen(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setHasBeenSeen(true);
					onVisibleRef.current?.();
				}
			},
			{ threshold: DEMO_START_VISIBILITY },
		);
		observer.observe(element);

		return () => observer.disconnect();
	}, [ref, hasBeenSeen]);

	return hasBeenSeen;
};

const CARD_HOVERED_DEBOUNCE_MS = 200;

export type UniformJsmPortalPlaygroundProps = {
	initialActiveTab?: TeamTabValue;
	cursorLabel?: string;
	cursorColor?: CursorColor;
	defaultTeam?: string;
	pagePath?: string;
	errorFallback?: ReactNode;
	errorFallbackRender?: (props: FallbackRenderProps) => ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
};

export const UniformJsmPortalPlayground = ({
	initialActiveTab = 'it-support',
	cursorLabel,
	cursorColor = 'yellow',
	defaultTeam,
	pagePath,
	errorFallback = null,
	errorFallbackRender,
	onError,
}: UniformJsmPortalPlaygroundProps = {}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experimentResult = useExperiment(wacRefreshJsm);
	const isPlaygroundSignupIntentEnabled =
		experimentResult.type !== 'enrolled' ? wacRefreshJsm.defaultValue : experimentResult.value;
	const { search } = useLocation();
	const [activeTab, setActiveTab] = useState<TeamTabValue>(
		() =>
			getPlaygroundTabForTeamType(new URLSearchParams(search).get('team_type')) ?? initialActiveTab,
	);
	const [isCursorActive, setIsCursorActive] = useState(false);
	const [isCursorHovering, setIsCursorHovering] = useState(false);
	const [isCursorClicking, setIsCursorClicking] = useState(false);
	const [hasUserTakenOver, setHasUserTakenOver] = useState(false);
	const isStaticMobile = useIsStaticMobile();
	const cursorRef = useRef<HTMLDivElement>(null);
	const isPointerInsidePreviewRef = useRef(false);
	const isHoveringInteractiveRef = useRef(false);
	const hasFiredViewRef = useRef(false);

	const fireSectionViewed = useCallback(() => {
		if (hasFiredViewRef.current) {
			return;
		}
		hasFiredViewRef.current = true;

		createAnalyticsEvent({
			actionSubject: 'jsmPlaygroundSection',
			action: 'viewed',
			eventType: 'ui',
			source: 'playground',
			attributes: {
				component: 'interactive-playground',
				page_path:
					pagePath ??
					(typeof window !== 'undefined' &&
					window.location?.pathname &&
					window.location.pathname !== '/'
						? window.location.pathname
						: '/software/jira/service-management'),
				defaultTeam: defaultTeam ?? initialActiveTab.replace(/-/g, '_'),
			},
		}).fire();
	}, [createAnalyticsEvent, defaultTeam, initialActiveTab, pagePath]);

	const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [previewColumnRef, previewScale] = usePreviewScale();
	const hasPreviewBeenSeen = useHasBeenSeen(previewColumnRef, fireSectionViewed);

	const fireCardHovered = useCallback(
		(currentTab: TeamTabValue) => {
			createAnalyticsEvent({
				actionSubject: 'card',
				action: 'hovered',
				actionSubjectId: 'previewCard',
				eventType: 'ui',
				source: 'jsm-playground',
				attributes: {
					trackEventName: 'hovered-preview-card',
					eventContainer: 'jsm-playground',
					team_type: currentTab.replace(/-/g, '_'),
					tabId: currentTab,
					page_canonical_url:
						typeof document !== 'undefined'
							? (document.querySelector('link[rel="canonical"]')?.getAttribute('href') ??
								'https://www.atlassian.com/software/jira/service-management')
							: 'https://www.atlassian.com/software/jira/service-management',
					page_path:
						pagePath ??
						(typeof window !== 'undefined' &&
						window.location?.pathname &&
						window.location.pathname !== '/'
							? window.location.pathname
							: '/software/jira/service-management'),
					loginUserDetected: Cookie.readCookie('login_user_detected') ?? 'false',
				},
			}).fire();
		},
		[createAnalyticsEvent, pagePath],
	);

	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	const {
		recordTabExplored,
		handlePointerDown: handleExploredPointerDown,
		handleClick: handleExploredClick,
		handleKeyDown: handleExploredKeyDown,
		handleChange: handleExploredChange,
	} = useJsmPlaygroundSectionExploredAnalytics({
		initialActiveTab,
	});

	const updateCursorPosition = (event: ReactPointerEvent<HTMLElement>) => {
		const cursor = cursorRef.current;

		if (!cursor) {
			return;
		}

		const sectionBounds = event.currentTarget.getBoundingClientRect();
		cursor.style.setProperty('--cursor-x', `${event.clientX - sectionBounds.left}px`);
		cursor.style.setProperty('--cursor-y', `${event.clientY - sectionBounds.top}px`);

		const isClickable = isClickableElement(event.target);
		if (isClickable !== isHoveringInteractiveRef.current) {
			isHoveringInteractiveRef.current = isClickable;
			setIsCursorHovering(isClickable);
		}
	};

	const handlePreviewPointerEnter = (event: ReactPointerEvent<HTMLDivElement>) => {
		isPointerInsidePreviewRef.current = true;
		updateCursorPosition(event);
		setIsCursorActive(true);

		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
		}
		hoverTimeoutRef.current = setTimeout(() => {
			if (isPointerInsidePreviewRef.current) {
				fireCardHovered(activeTab);
			}
		}, CARD_HOVERED_DEBOUNCE_MS);
	};

	const handlePreviewPointerLeave = () => {
		isPointerInsidePreviewRef.current = false;
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
		isHoveringInteractiveRef.current = false;
		setIsCursorActive(false);
		setIsCursorHovering(false);
		setIsCursorClicking(false);
	};

	const handlePreviewFocusChange = () => {
		if (!isPointerInsidePreviewRef.current) {
			isHoveringInteractiveRef.current = false;
			setIsCursorActive(false);
			setIsCursorHovering(false);
			setIsCursorClicking(false);
		}
	};

	const handlePreviewFocus = () => {
		setHasUserTakenOver(true);
		handlePreviewFocusChange();
	};

	const handlePreviewKeyDown = () => {
		setHasUserTakenOver(true);
	};

	const handlePreviewPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
		setHasUserTakenOver(true);
		if (isClickableElement(event.target)) {
			setIsCursorClicking(true);
		}
	};

	const handlePreviewPointerUp = () => {
		setIsCursorClicking(false);
	};

	const [teamVisits, setTeamVisits] = useState<Partial<Record<TeamTabValue, number>>>({});
	const [completedDemoTab, setCompletedDemoTab] = useState<TeamTabValue | null>(null);
	const activeTabRef = useRef(activeTab);
	activeTabRef.current = activeTab;
	const onDemoComplete = useMemo(() => {
		const complete = (team: TeamTabValue) => () => {
			setCompletedDemoTab((current) => (activeTabRef.current === team ? team : current));
		};
		return {
			'it-support': complete('it-support'),
			'it-operations': complete('it-operations'),
			engineering: complete('engineering'),
			hr: complete('hr'),
		};
	}, []);
	const isDemoComplete = completedDemoTab === activeTab;
	const [demoLayer, setDemoLayer] = useState<HTMLDivElement | null>(null);
	const { handlePreviewClick } = useJsmPlaygroundAnalytics(activeTab);

	const sectionRef = useRef<HTMLElement>(null);
	const previewCardRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const section = sectionRef.current;
		if (!section) {
			return;
		}

		const onClick = (e: MouseEvent) => handleExploredClick(e);
		const onPointerDown = (e: PointerEvent) => handleExploredPointerDown(e);
		const onKeyDown = (e: KeyboardEvent) => handleExploredKeyDown(e);
		const onChange = (e: Event) => handleExploredChange(e);

		section.addEventListener('click', onClick);
		section.addEventListener('pointerdown', onPointerDown);
		section.addEventListener('keydown', onKeyDown);
		section.addEventListener('change', onChange);

		return () => {
			section.removeEventListener('click', onClick);
			section.removeEventListener('pointerdown', onPointerDown);
			section.removeEventListener('keydown', onKeyDown);
			section.removeEventListener('change', onChange);
		};
	}, [handleExploredClick, handleExploredPointerDown, handleExploredKeyDown, handleExploredChange]);

	useEffect(() => {
		const previewCard = previewCardRef.current;
		if (!previewCard) {
			return;
		}

		const onCardClick = (e: MouseEvent) => handlePreviewClick(e);

		previewCard.addEventListener('click', onCardClick);

		return () => {
			previewCard.removeEventListener('click', onCardClick);
		};
	}, [handlePreviewClick]);

	// The shared Tab links to `#<team>`, which the browser follows if a tab is clicked
	// before hydration. `team_type` is the source of truth, so drop that hash.
	useEffect(() => {
		const currentUrl = new URL(window.location.href);
		if (teamTabIds.has(currentUrl.hash.slice(1))) {
			currentUrl.hash = '';
			history.replaceState(history.state, '', currentUrl);
		}
	}, []);

	const handleTabSelect = (id: string) => {
		const team = id as TeamTabValue;
		if (isPlaygroundSignupIntentEnabled) {
			const currentUrl = new URL(window.location.href);
			setJsmPlaygroundSignupIntent(currentUrl.searchParams, playgroundTeamTypes[team]);
			history.replaceState({}, '', currentUrl);
			window.dispatchEvent(new Event(JSM_PLAYGROUND_SIGNUP_INTENT_CHANGE_EVENT));
		}
		if (team !== activeTab) {
			setTeamVisits((visits) => ({ ...visits, [team]: (visits[team] ?? 0) + 1 }));
			setCompletedDemoTab(null);
		}
		recordTabExplored(team);
		setHasUserTakenOver(false);
		setActiveTab(team);
	};

	return (
		<section
			ref={sectionRef}
			css={playgroundSectionStyles}
			aria-label={intl.formatMessage(messages.sectionLabel)}
		>
			<div css={tabListWrapperStyles}>
				<TabList
					activeTabId={activeTab}
					aria-label={intl.formatMessage(messages.serviceTeamTabListLabel)}
				>
					{teamTabs.map((teamTab, index) => (
						<Tab
							key={teamTab.value}
							tabId={teamTab.value}
							index={index}
							title={intl.formatMessage(teamTab.label)}
							isActive={teamTab.value === activeTab}
							onTabSelect={handleTabSelect}
							tabSize="small"
						/>
					))}
				</TabList>
			</div>

			<div css={previewOuterStyles} ref={previewColumnRef}>
				<JsmPlaygroundCursor
					ref={cursorRef}
					label={cursorLabel ?? intl.formatMessage(messages.customCursorLabel)}
					color={cursorColor}
					isActive={isCursorActive && !isStaticMobile}
					isHovering={isCursorHovering}
					isClicking={isCursorClicking}
				/>
				<div
					css={previewScaleFrameStyles}
					style={{ height: `${PREVIEW_DESIGN_HEIGHT * previewScale}px` }}
				>
					<div
						css={previewScaleInnerStyles}
						data-preview-scale={previewScale}
						style={{ transform: `scale(${previewScale})` }}
					>
						<JsmPlaygroundDemoLayerContext.Provider value={demoLayer}>
							{/* The handlers here only drive the fake cursor overlay and hand control back to
							    the user; the real interactive elements are the mock product UI inside, so this
							    wrapper carries no semantics of its own. */}
							<div
								ref={previewCardRef}
								css={[previewCardStyles, isStaticMobile && staticPreviewCardStyles]}
								data-testid="jsm-playground-preview"
								// @ts-expect-error -- inert is missing from React types
								inert={isStaticMobile ? '' : undefined}
								role="none"
								onBlur={handlePreviewFocusChange}
								onFocus={handlePreviewFocus}
								onKeyDown={handlePreviewKeyDown}
								onPointerDown={handlePreviewPointerDown}
								onPointerEnter={handlePreviewPointerEnter}
								onPointerLeave={handlePreviewPointerLeave}
								onPointerMove={updateCursorPosition}
								onPointerUp={handlePreviewPointerUp}
							>
								<div css={panelStackStyles}>
									{teamTabs.map((teamTab) => (
										<TabPanel
											key={teamTab.value}
											id={teamTab.value}
											isActive={teamTab.value === activeTab}
											// @ts-expect-error -- inert is missing from React types
											inert={teamTab.value === activeTab ? undefined : ''}
										>
											<JsmPlaygroundErrorBoundary
												teamType={teamTab.value}
												fallback={errorFallback}
												fallbackRender={errorFallbackRender}
												onError={onError}
												resetKeys={[teamVisits[teamTab.value]]}
											>
												{teamTab.value === 'it-support' && (
													<JsmPortalPlaygroundItSupportPreview
														key={teamVisits['it-support'] ?? 0}
														isActive={activeTab === 'it-support' && hasPreviewBeenSeen}
														hasUserTakenOver={hasUserTakenOver}
														onDemoComplete={onDemoComplete['it-support']}
													/>
												)}
												{teamTab.value === 'it-operations' && (
													<div css={teamPreviewStyles}>
														<JsmPlaygroundNavigation
															config={teamNavigationConfig[teamTab.value]}
															teamName={intl.formatMessage(teamTab.label)}
														/>
														{activeTab === 'it-operations' && hasPreviewBeenSeen ? (
															<JsmPlaygroundItOperations
																hasUserTakenOver={hasUserTakenOver}
																onDemoComplete={onDemoComplete['it-operations']}
															/>
														) : (
															<div css={blankPreviewStyles} />
														)}
													</div>
												)}
												{teamTab.value === 'engineering' && (
													<div css={teamPreviewStyles}>
														<JsmPlaygroundNavigation
															config={teamNavigationConfig[teamTab.value]}
															teamName={intl.formatMessage(teamTab.label)}
														/>
														{activeTab === 'engineering' && hasPreviewBeenSeen ? (
															<JsmPlaygroundEngineering
																hasUserTakenOver={hasUserTakenOver}
																onDemoComplete={onDemoComplete.engineering}
															/>
														) : (
															<div css={blankPreviewStyles} />
														)}
													</div>
												)}
												{teamTab.value === 'hr' && (
													<JsmPortalPlaygroundHrPreview
														key={teamVisits.hr ?? 0}
														isActive={activeTab === 'hr' && hasPreviewBeenSeen}
														hasUserTakenOver={hasUserTakenOver}
														onDemoComplete={onDemoComplete.hr}
													/>
												)}
											</JsmPlaygroundErrorBoundary>
										</TabPanel>
									))}
								</div>
							</div>
						</JsmPlaygroundDemoLayerContext.Provider>
						<div ref={setDemoLayer} css={demoLayerStyles} aria-hidden="true" />
						{hasPreviewBeenSeen && isDemoComplete && <JsmPlaygroundSeeHowItWorks key={activeTab} />}
					</div>
				</div>
			</div>
		</section>
	);
};

export const UniformJsmPortalPlaygroundContent = UniformJsmPortalPlayground;

export default UniformJsmPortalPlayground;

const playgroundSectionStyles = css({
	position: 'relative',
	boxSizing: 'border-box',
	width: '100%',
	overflowX: 'clip',
	paddingBlockStart: token('space.400'),
	paddingBlockEnd: token('space.600'),
	paddingInline: token('space.300'),
	[media.above.md]: {
		paddingInline: token('space.600'),
	},
});

const tabListWrapperStyles = css({
	display: 'flex',
	justifyContent: 'center',
	width: 'fit-content',
	maxWidth: '100%',
	marginInline: 'auto',
	marginBlockEnd: token('space.400'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		border: `1px solid ${wacToken('color.primary.black')} !important`,
		[media.below.sm]: {
			backgroundColor: 'transparent !important',
			border: 'none !important',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a[role="tab"]': {
		paddingInline: `${token('space.250')} !important`,
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: token('font.weight.semibold'),
	},
});

const panelStackStyles = css({
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr)',
	gridTemplateRows: 'minmax(0, 1fr)',
	width: '100%',
	height: '100%',
	minHeight: 0,
});

const previewOuterStyles = css({
	position: 'relative',
	width: '100%',
	maxWidth: `${PREVIEW_DESIGN_WIDTH}px`,
	marginInline: 'auto',
	boxSizing: 'border-box',
});

const previewScaleFrameStyles = css({
	position: 'relative',
	width: '100%',
});

const previewScaleInnerStyles = css({
	width: `${PREVIEW_DESIGN_WIDTH}px`,
	height: `${PREVIEW_DESIGN_HEIGHT}px`,
	transformOrigin: 'top left',
});

const demoLayerStyles = css({
	position: 'absolute',
	inset: 0,
	zIndex: 2,
	pointerEvents: 'none',
});

const PRODUCT_FONT_STACK =
	'"Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

const previewCardStyles = css({
	position: 'relative',
	zIndex: 1,
	boxSizing: 'border-box',
	width: '100%',
	height: `${PREVIEW_DESIGN_HEIGHT}px`,
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
	// The design-system overlay shadow includes a 1px hairline, which reads as a hard
	// edge on this large card. The prototype uses one long, soft shadow instead.
	// eslint-disable-next-line local/ensure-wac-token-usage
	boxShadow: '0 4px 32px rgb(0 0 0 / 12%)',
	overflow: 'hidden',
	cursor: 'none',
	font: token('font.body', `normal 400 14px/20px ${PRODUCT_FONT_STACK}`),
	fontFamily: token('font.family.body', PRODUCT_FONT_STACK),
	color: token('color.text'),
	// The prototype antialiases the whole preview. Subpixel smoothing makes the
	// 24px regular hero subtitle look heavier than that preview.
	WebkitFontSmoothing: 'antialiased',
	MozOsxFontSmoothing: 'grayscale',
	// The playground is product UI rendered inside the marketing site, so it has to
	// undo two things the page applies to bare tags. `index.css` sets Charlie on
	// `button`, `input`, `h1`–`h6`, and `<TypographyStyles>` sets the Charlie family
	// *and marketing sizes* (an `h2` is 2.5rem) through a descendant selector that
	// outranks the class ADS puts on the element. Each rule below is repeated to lift
	// it past that selector.
	// Only the family is reset so ADS controls keep their own weight and size (an ADS
	// button label is `font.weight.medium`).
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&&& button, &&& input, &&& select, &&& textarea': {
		fontFamily: 'inherit',
	},
	// No heading the playground renders is a bare tag — `<JsmPlaygroundHeading>` renders a
	// `div` precisely so the descendant selector cannot reach it — so `p` is the only arm
	// here doing routine work. `h1`–`h6` stay as a backstop for bare headings coming out of
	// a third-party component, where inheriting the product body type is the closer miss.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&&& p, &&& h1, &&& h2, &&& h3, &&& h4, &&& h5, &&& h6': {
		fontFamily: 'inherit',
		fontSize: 'inherit',
		fontWeight: 'inherit',
		lineHeight: 'inherit',
		letterSpacing: 'inherit',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'*': {
		cursor: 'none !important',
	},
	'@media (pointer: coarse)': {
		cursor: 'auto',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'*': {
			cursor: 'auto !important',
		},
	},
});

const staticPreviewCardStyles = css({
	pointerEvents: 'none',
});

const blankPreviewStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flex: '1 1 auto',
	width: '100%',
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
});

const teamPreviewStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	borderRadius: token('radius.xlarge'),
	overflow: 'hidden',
});

const isClickableElement = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) {
		return false;
	}

	const clickableSelector = [
		'button',
		'a[href]',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'label',
		'[role="button"]',
		'[role="checkbox"]',
		'[role="tab"]',
		'[role="link"]',
		'[data-clickable="true"]',
	].join(', ');

	return Boolean(target.closest(clickableSelector));
};
